import React, { useMemo, useState } from 'react';
import { GenerationTransaction, UsageByType, PaginationInfo } from '../../services/creditTransactionService';
import TransactionCard from './TransactionCard';
import Pagination from './Pagination';

// Helper function to check if a generation was successful
const isGenerationSuccessful = (transaction: GenerationTransaction): boolean => {
    const details = transaction.details;
    if (!details) return false;

    // For video generations, check if videoUrls is not empty
    if (transaction.generation_type === 'video-generation' || transaction.generation_type === 'video_generation') {
        return details.videoUrls && details.videoUrls.length > 0;
    }

    // For image generations (photoshoot, try-on, fashion_extractor, ai-poses, background-removal, ai-workflow), check if imageUrls is not empty
    return details.imageUrls && details.imageUrls.length > 0;
};

interface GenerationHistoryTabProps {
    generationHistory: GenerationTransaction[];
    usageByType: UsageByType;
    dateRange: { start: Date | null; end: Date | null };
    searchTerm: string;
}

const GenerationHistoryTab: React.FC<GenerationHistoryTabProps> = ({
    generationHistory,
    usageByType,
    dateRange,
    searchTerm
}) => {
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Filter transactions based on date range and search term
    const filteredTransactions = useMemo(() => {
        const filtered = generationHistory.filter(transaction => {
            // Date filter
            const transactionDate = new Date(transaction.timestamp);
            if (!isNaN(transactionDate.getTime())) {
                if (dateRange.start && transactionDate < dateRange.start) return false;
                if (dateRange.end && transactionDate > dateRange.end) return false;
            }

            // Search filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                return (
                    transaction.generation_type.toLowerCase().includes(searchLower) ||
                    transaction.display_name.toLowerCase().includes(searchLower) ||
                    transaction.prompt.toLowerCase().includes(searchLower)
                );
            }

            return true;
        });

        // Sort by timestamp (newest first)
        return filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }, [generationHistory, dateRange, searchTerm]);

    // Reset to first page when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [dateRange, searchTerm]);

    // Calculate pagination info
    const paginationInfo: PaginationInfo = useMemo(() => {
        const totalItems = filteredTransactions.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const hasNextPage = currentPage < totalPages;
        const hasPreviousPage = currentPage > 1;

        return {
            currentPage,
            totalPages,
            totalItems,
            itemsPerPage,
            hasNextPage,
            hasPreviousPage
        };
    }, [filteredTransactions.length, itemsPerPage, currentPage]);

    // Get current page transactions
    const currentPageTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredTransactions.slice(startIndex, endIndex);
    }, [filteredTransactions, currentPage, itemsPerPage]);

    const getGenerationIcon = (type: string) => {
        const iconMap: Record<string, { icon: string; color: string; bg: string }> = {
            // Handle generation_type values (from individual transactions)
            'photoshoot': { icon: 'fas fa-box', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'try-on': { icon: 'fas fa-user-circle', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'realistic-photoshoot': { icon: 'fas fa-camera', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'realistic-try-on': { icon: 'fas fa-camera', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'fashion_extractor': { icon: 'fas fa-cut', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ghost_mannequin': { icon: 'fas fa-user-secret', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ghost-mannequin': { icon: 'fas fa-user-secret', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai_ghost_mannequin': { icon: 'fas fa-user-secret', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai-ghost-mannequin': { icon: 'fas fa-user-secret', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai_wrinkle_removal': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'wrinkle-removal': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'video-generation': { icon: 'fas fa-film', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'video_generation': { icon: 'fas fa-film', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'canvas-editor': { icon: 'fas fa-paint-brush', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'canvas_editor': { icon: 'fas fa-paint-brush', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai-poses': { icon: 'fas fa-user-ninja', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'background-removal': { icon: 'fas fa-eraser', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'background-change': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai-background-change': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai-designer': { icon: 'fas fa-drafting-compass', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'image-editing': { icon: 'fas fa-edit', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'upscaler': { icon: 'fas fa-expand-arrows-alt', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'ai-workflow': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'stitched-cloths': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'vton': { icon: 'fas fa-tshirt', color: 'text-secondary-600', bg: 'bg-secondary-100' },

            // Handle display_name values (from usage breakdown) - matching sidebar icons
            'AI Photoshoot': { icon: 'fas fa-box', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Clothes AI Photoshoot': { icon: 'fas fa-user-circle', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Product AI Photoshoot': { icon: 'fas fa-box', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Advance AI Photoshoot': { icon: 'fas fa-camera', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Try-On': { icon: 'fas fa-user-circle', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Virtual Try-On': { icon: 'fas fa-user-circle', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Fashion Extractor': { icon: 'fas fa-cut', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Outfit Extractor (Flatlay)': { icon: 'fas fa-cut', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Ghost Mannequin': { icon: 'fas fa-user-secret', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Wrinkle Removal': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Video Generation': { icon: 'fas fa-film', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Canvas Editor': { icon: 'fas fa-paint-brush', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'AI Poses': { icon: 'fas fa-user-ninja', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Background Removal': { icon: 'fas fa-eraser', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Background Change': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'AI Background Change': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'AI Designer': { icon: 'fas fa-drafting-compass', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Image Editing': { icon: 'fas fa-edit', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Image Upscaler': { icon: 'fas fa-expand-arrows-alt', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'AI Workflow': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'Stitched Cloths': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'AI Workflow - Stitched Cloths': { icon: 'fas fa-wand-magic-sparkles', color: 'text-secondary-600', bg: 'bg-secondary-100' },
            'VTON': { icon: 'fas fa-tshirt', color: 'text-secondary-600', bg: 'bg-secondary-100' },
        };

        // Try exact match first, then lowercase
        return iconMap[type] || iconMap[type.toLowerCase()] || {
            icon: 'fas fa-wand-magic-sparkles',
            color: 'text-secondary-600',
            bg: 'bg-secondary-100'
        };
    };

    const formatGenerationType = (type: string) => {
        const typeMap: Record<string, string> = {
            photoshoot: 'AI Photoshoot',
            'realistic-photoshoot': 'Advance AI Photoshoot',
            'realistic-try-on': 'Advance AI Photoshoot',
            tryon: 'Virtual Try-On',
            'try-on': 'Virtual Try-On',
            fashion_extractor: 'Outfit Extractor (Flatlay)',
            ghost_mannequin: 'Ghost Mannequin',
            'ghost-mannequin': 'Ghost Mannequin',
            ai_ghost_mannequin: 'Ghost Mannequin',
            'ai-ghost-mannequin': 'Ghost Mannequin',
            ai_wrinkle_removal: 'Wrinkle Removal',
            'wrinkle-removal': 'Wrinkle Removal',
            'video-generation': 'Video Generation',
            video_generation: 'Video Generation',
            'canvas-editor': 'Canvas Editor',
            canvas_editor: 'Canvas Editor',
            'ai-poses': 'AI Poses',
            'background-removal': 'Background Removal',
            'ai-designer': 'AI Designer',
            'image-editing': 'Image Editing',
            upscaler: 'Image Upscaler',
            'ai-workflow': 'AI Workflow',
            'stitched-cloths': 'Stitched Cloths',
            vton: 'Virtual Try-On (VTON)'
        };

        return typeMap[type.toLowerCase()] || type;
    };

    // Group display_name for usage breakdown: unify "AI Workflow - *" under "AI Workflow"
    const getUsageBreakdownKey = (displayName: string): string => {
        if (!displayName) return displayName;
        const d = displayName.trim();
        if (d.startsWith('AI Workflow - ') || d === 'AI Workflow') return 'AI Workflow';
        return displayName;
    };

    // Calculate usage statistics excluding failed generations
    const usageStats = useMemo(() => {
        // Recalculate usage stats from actual successful transactions
        const successfulTransactions = generationHistory.filter(isGenerationSuccessful);
        const recalculatedUsageByType: Record<string, { count: number; total_credits: number }> = {};

        successfulTransactions.forEach(transaction => {
            const groupKey = getUsageBreakdownKey(transaction.display_name);
            if (!recalculatedUsageByType[groupKey]) {
                recalculatedUsageByType[groupKey] = { count: 0, total_credits: 0 };
            }
            recalculatedUsageByType[groupKey].count += 1;
            recalculatedUsageByType[groupKey].total_credits += transaction.credits_used;
        });

        const total = Object.values(recalculatedUsageByType).reduce((sum, typeData) => sum + typeData.total_credits, 0);
        const sortedTypes = Object.entries(recalculatedUsageByType)
            .filter(([_, typeData]) => typeData.total_credits > 0)
            .sort(([, a], [, b]) => b.total_credits - a.total_credits);

        return { total, sortedTypes };
    }, [generationHistory]);

    if (filteredTransactions.length === 0 && usageStats.total === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <i className="fas fa-magic text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-text-heading mb-2">
                    No generation history found
                </h3>
                <p className="text-text-description">
                    {searchTerm || dateRange.start || dateRange.end
                        ? "Try adjusting your filters to find generations."
                        : "Start creating with AI to see your usage history here."}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full">
            {/* Usage Summary */}
            {usageStats.sortedTypes.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4">
                    <h3 className="text-sm font-medium text-text-heading mb-3">Usage Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {usageStats.sortedTypes.map(([type, typeData]) => {
                            const iconInfo = getGenerationIcon(type);
                            return (
                                <div key={type} className="flex items-center space-x-3">
                                    <div className={`w-8 h-8 rounded-full ${iconInfo.bg} flex items-center justify-center`}>
                                        <i className={`${iconInfo.icon} text-sm ${iconInfo.color}`}></i>
                                    </div>
                                    <div>
                                        <p className="text-xs text-text-description">{type}</p>
                                        <p className="text-sm font-medium text-text-heading">{typeData.total_credits} credits</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Transactions */}
            <div className="space-y-4">
                {currentPageTransactions.map((transaction) => {
                    const iconInfo = getGenerationIcon(transaction.generation_type);
                    const isSuccessful = isGenerationSuccessful(transaction);

                    // Use details from API
                    const numberOfImages = transaction.details.numberOfImages || 0;
                    const numberOfVideos = transaction.details.numberOfVideos || 0;

                    // Simplified subtitle for collapsed view
                    const subtitle = [
                        numberOfImages > 0 && `${numberOfImages} ${numberOfImages === 1 ? 'image' : 'images'}`,
                        numberOfVideos > 0 && `${numberOfVideos} ${numberOfVideos === 1 ? 'video' : 'videos'}`
                    ].filter(Boolean).join(', ') || 'Generation';

                    return (
                        <TransactionCard
                            key={transaction.id}
                            transaction={{
                                id: transaction.id,
                                date: transaction.timestamp,
                                type: 'usage',
                                title: transaction.display_name || formatGenerationType(transaction.generation_type),
                                subtitle: subtitle,
                                amount: transaction.credits_used,
                                prompt: transaction.prompt || 'No prompt available',
                                icon: iconInfo.icon,
                                iconColor: iconInfo.color,
                                iconBg: iconInfo.bg,
                                generationType: transaction.generation_type,
                                details: transaction.details,
                                status: isSuccessful ? 'completed' : 'failed',
                                isGenerationSuccessful: isSuccessful
                            }}
                        />
                    );
                })}
            </div>

            {paginationInfo.totalPages > 1 && (
                <Pagination
                    currentPage={paginationInfo.currentPage}
                    totalPages={paginationInfo.totalPages}
                    totalItems={paginationInfo.totalItems}
                    itemsPerPage={paginationInfo.itemsPerPage}
                    hasNextPage={paginationInfo.hasNextPage}
                    hasPreviousPage={paginationInfo.hasPreviousPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(newItemsPerPage) => {
                        setItemsPerPage(newItemsPerPage);
                        setCurrentPage(1);
                    }}
                />
            )}
        </div>
    );
};

export default GenerationHistoryTab; 